import { DisconnectReason } from '@yandex-360/telemost-sdk';
import { call, fork, put, select, take, takeLatest } from 'typed-redux-saga';

import { SELF } from '../../../../shared/constants';
import { getRoomAgentConfig } from '../../../../shared/features/config/selectors';
import { goloomSdk } from '../../../../shared/features/goloom-sdk';
import { mapConfPayloadToHaltDescriptor } from '../../../../shared/features/room-agent/sagas';
import { ChannelHolder } from '../../../../shared/helpers/channelHolder';
import { createLogger } from '../../../../shared/helpers/loggerFacade';
import { recordHelpers } from '../../sagas/root';
import {
    disconnectConferenceAction,
    disconnectedFromConferenceDoneAction,
    updateParticipant,
} from '../conference/slice';
import { clearOverlayManager } from '../record/drawer/draw-conference-cached';
import { clearRvfcSkip } from '../record/drawer/rvfc-skip/optimizer';
import { SignalingAPI } from './api/SignalingAPI';
import { RoomAgentHaltError } from './errors';
import { getRecorderResolution } from './helpers';
import { RecorderVideo } from './recorder/RecorderVideo';
import { debugSaga } from './sagas/debug';
import { dtmfSaga } from './sagas/dtmf';
import { recordSaga } from './sagas/record';
import { setViewSaga } from './sagas/set-view';
import { statsSaga } from './sagas/stats';

const logger = createLogger('goloom:room-agent');

const roomAgentHaltReasons = new Set([
    DisconnectReason.UNAUTHORIZED,
    DisconnectReason.UNKNOWN,
    DisconnectReason.CLOSE_ROOM,
    DisconnectReason.CLOSE_ROOM,
    DisconnectReason.KICK,
]);

const setLocalTrack = function* (track: MediaStreamTrack) {
    const localTrack = yield* call([goloomSdk, goloomSdk.replaceLocalTrack], track, true);
    if (localTrack) {
        yield put(
            updateParticipant({
                id: SELF,
                [localTrack.kind]: {
                    id: localTrack.id,
                    muted: false,
                },
            })
        );
    }
};

export function* initRoomAgentSaga() {
    logger.debug('init');

    const config = yield* select(getRoomAgentConfig);
    const debugMode = config?.debug;

    const channelHolder = new ChannelHolder();
    const signaling = new SignalingAPI(debugMode);

    try {
        logger.debug('ready sending');
        yield* call([signaling, signaling.ready]);

        yield* takeLatest(signaling.terminateEventChannel, function* (event) {
            logger.debug('handling terminate signal');

            try {
                yield* put(
                    disconnectConferenceAction({
                        source: DisconnectReason.UNKNOWN,
                    })
                );
                yield* take(disconnectedFromConferenceDoneAction);
            } catch (error) {
                logger.error(error, {
                    method: 'signaling.terminateEventChannel',
                });
            } finally {
                event.resolve();
            }
        });

        const recorderVideo = new RecorderVideo({
            resolution: getRecorderResolution(config?.recorderResolution),
            h264ProfileLevelId: config?.h264ProfileLevelId,
        });

        yield* call(setLocalTrack, recorderVideo.getTrack());

        yield* channelHolder.takeEvery(goloomSdk.onInit, function* () {
            logger.debug('set local track');
            yield* call(setLocalTrack, recorderVideo.getTrack());
        });

        yield* fork(dtmfSaga, { signaling });
        yield* fork(setViewSaga, { signaling });
        yield* fork(debugSaga, { signaling });
        yield* fork(recordSaga, { recorderVideo, helpers: recordHelpers, logger });
        yield* fork(statsSaga, { signaling, channelHolder });

        const disconnectDoneResult = yield* take(disconnectedFromConferenceDoneAction);

        if (disconnectDoneResult.payload && roomAgentHaltReasons.has(disconnectDoneResult.payload?.source)) {
            const descriptor = mapConfPayloadToHaltDescriptor(disconnectDoneResult.payload);

            throw new RoomAgentHaltError(descriptor.reason, descriptor.description, {
                method: 'rootRoomAgentSaga',
            });
        }
    } catch (error) {
        const normalizedError = RoomAgentHaltError.from(error, {
            method: 'rootRoomAgentSaga',
        });

        logger.error(normalizedError);

        yield* call([signaling, signaling.halt], {
            reason: normalizedError.reason,
            description: normalizedError.description,
        });
        yield* put(
            disconnectConferenceAction({
                source: DisconnectReason.UNKNOWN,
                error: {
                    description: normalizedError.description,
                },
            })
        );
    } finally {
        clearOverlayManager();
        clearRvfcSkip();
        channelHolder.close();
        logger.debug('close room-agent');
    }
}
